// Mirrors the backend's DASHBOARD_WIDGET_IDS (src/constants/dashboardWidgets.ts
// in BE-project-management-app) — must stay in sync. Labels are i18n'd here
// (dashboard.widgets.<id>); `link` is the "view all" target for widgets
// where a real filter exists on the destination page — omitted where it
// doesn't map to a real filter (still a useful shortcut to the page).
export interface DashboardWidgetDef {
  id: string;
  labelKey: string;
  // 'workload' and 'activity' aren't single values — they render the same
  // richer content the fixed dashboard always showed (status breakdown /
  // recent activity feed), sourced from GET /dashboard/home instead of
  // the plain widget-values endpoint.
  format: 'count' | 'percent' | 'minutes' | 'workload' | 'activity';
  link?: string;
  // Wider widgets (workload/activity) get more grid room than a plain stat card.
  wide?: boolean;
}

export const DASHBOARD_WIDGETS: DashboardWidgetDef[] = [
  { id: 'open_tasks_total', labelKey: 'dashboard.widgets.openTasksTotal', format: 'count', link: '/tasks' },
  { id: 'sla_attainment_pct', labelKey: 'dashboard.widgets.slaAttainmentPct', format: 'percent', link: '/incidents' },
  { id: 'avg_resolution_minutes', labelKey: 'dashboard.widgets.avgResolutionMinutes', format: 'minutes', link: '/incidents' },
  { id: 'claimed_today', labelKey: 'dashboard.widgets.claimedToday', format: 'count', link: '/tasks?status=claimed' },
  { id: 'my_queue_count', labelKey: 'dashboard.widgets.myQueueCount', format: 'count', link: '/tasks?assignee=me&status=open' },
  { id: 'my_open_tasks', labelKey: 'dashboard.widgets.myOpenTasks', format: 'count', link: '/tasks?assignee=me&status=open' },
  { id: 'group_open_tasks', labelKey: 'dashboard.widgets.groupOpenTasks', format: 'count', link: '/tasks?group=mine&status=open' },
  { id: 'my_unclaimed_tasks', labelKey: 'dashboard.widgets.myUnclaimedTasks', format: 'count', link: '/tasks?assignee=me&status=todo' },
  { id: 'my_open_incidents', labelKey: 'dashboard.widgets.myOpenIncidents', format: 'count', link: '/incidents?reportedByMe=true' },
  { id: 'group_open_incidents', labelKey: 'dashboard.widgets.groupOpenIncidents', format: 'count', link: '/incidents?group=mine' },
  { id: 'problem_incidents', labelKey: 'dashboard.widgets.problemIncidents', format: 'count', link: '/incidents?status=problem' },
  { id: 'sla_at_risk_incidents', labelKey: 'dashboard.widgets.slaAtRiskIncidents', format: 'count', link: '/incidents?sla=at_risk' },
  { id: 'sla_breached_incidents', labelKey: 'dashboard.widgets.slaBreachedIncidents', format: 'count', link: '/incidents?sla=breached' },
  { id: 'my_created_today_tasks', labelKey: 'dashboard.widgets.myCreatedTodayTasks', format: 'count', link: '/tasks?createdByMe=true' },
  { id: 'my_created_today_incidents', labelKey: 'dashboard.widgets.myCreatedTodayIncidents', format: 'count', link: '/incidents?reportedByMe=true' },
  { id: 'my_completed_last7d_tasks', labelKey: 'dashboard.widgets.myCompletedLast7dTasks', format: 'count', link: '/tasks?assignee=me&status=completed' },
  { id: 'incidents_created_last7d', labelKey: 'dashboard.widgets.incidentsCreatedLast7d', format: 'count', link: '/incidents' },
  { id: 'my_watched_open_tasks', labelKey: 'dashboard.widgets.myWatchedOpenTasks', format: 'count', link: '/tasks' },
  { id: 'pending_invitations', labelKey: 'dashboard.widgets.pendingInvitations', format: 'count', link: '/settings?section=invitations' },
  { id: 'unassigned_tasks', labelKey: 'dashboard.widgets.unassignedTasks', format: 'count', link: '/tasks?unassigned=true' },
  { id: 'workload_by_status', labelKey: 'dashboard.widgets.workloadByStatus', format: 'workload', wide: true },
  { id: 'recent_activity', labelKey: 'dashboard.widgets.recentActivity', format: 'activity', wide: true }
];

export const MAX_DASHBOARD_WIDGETS = 10;

// Matches what the page always showed before customization existed, so
// a user who hasn't touched Settings yet sees the same thing as before.
export const DEFAULT_WIDGET_IDS = [
  'open_tasks_total',
  'claimed_today',
  'sla_attainment_pct',
  'avg_resolution_minutes',
  'workload_by_status',
  'recent_activity'
];
